import React, { FunctionComponent, useMemo } from 'react';

import transparentize from 'polished/lib/color/transparentize';

import Flex from '@gojob/ui-bits/Flex';
import usePalette from '@gojob/ui-bits/styles/PaletteProvider/usePalette';
import useTheme from '@gojob/ui-bits/styles/useTheme';
import Text from '@gojob/ui-bits/Text';

const alpha = transparentize(0.5);

export type QuoteProps = {
  author?: string;
  children: React.ReactNode;
};

const useStyles = () => {
  const { spacing, colors, fonts } = useTheme();
  const { background, highlight } = usePalette();

  return useMemo(() => {
    return {
      root: {
        display: 'inline-flex',
        position: 'relative',
        padding: spacing(2, 4),
        backgroundColor: colors[background],
        borderRadius: spacing(2),
        ':after': {
          position: 'absolute',
          content: '"‘‘"', // that's a lot of quotes 😅
          top: spacing(-5),
          left: spacing(2),
          color: alpha(colors[highlight]),
          ...fonts.headline1,
          letterSpacing: spacing(-1.5),
          lineHeight: 1,
          fontWeight: 800,
          fontSize: spacing(24),
        },
      },
      content: {
        zIndex: 1,
        flexDirection: 'column',
      },
      quote: {
        margin: spacing(2, 0),
        whiteSpace: 'pre-wrap',
      },
      author: {
        fontStyle: 'italic',
      },
    } as const;
  }, [spacing, colors, fonts, background, highlight]);
};

const Quote: FunctionComponent<QuoteProps> = (props) => {
  const { author, children } = props;
  const classes = useStyles();

  return (
    <Flex css={classes.root} margin={[4, 0]}>
      <Flex as="article" css={classes.content} flexDirection="column" justifyContent="space-between">
        <Text<'blockquote'> as="blockquote" variant="message2" css={classes.quote}>
          {children}
        </Text>
        {author ? (
          <Flex as="footer" margin={[2, 0, 0]} flexDirection="column">
            <Text css={classes.author} variant="message4">
              {author}
            </Text>
          </Flex>
        ) : null}
      </Flex>
    </Flex>
  );
};

export default Quote;
